import { useCallback, useMemo } from 'react';

import { ApiConfiguredNotification } from 'api/notification/types/ApiConfiguredNotification';
import { ApiNotification } from 'api/notification/types/ApiNotification';
import { NotificationsDropdown } from 'components/NotificationsDropdown/NotificationsDropdown';
import { useFetchNotificationsOnPageChange } from 'hooks/useFetchNotificationsOnPageChange';
import { LocalDropdownNotificationType } from 'types/LocalNotification/LocalDropdownNotificationType';
import { LocalNotificationStatus } from 'types/LocalNotification/LocalNotificationStatus';
import { MappedLocalNotification } from 'types/LocalNotification/MappedLocalNotification';
import { MappedNotification } from 'types/mappedNotifications/MappedNotification';
import { trackClicked } from 'utils/analytics/track/trackClicked';
import { trackEvent } from 'utils/analytics/track/trackEvent';
import { promoteAts } from 'utils/ats';
import {
  promoteAdminSurvey2024,
  promoteEsSurvey,
  promoteGradfair2024,
  promoteOfficeHours,
  promoteOfficeHoursApril24,
  promoteResumeWebinar,
  promoteWebinarAugust2024,
} from 'utils/notifications';
import {
  markLocalNotificationsAsViewed,
  useLocalNotification,
  useMappedLocalNotifications,
} from 'zustand-stores/localNotificationsStore';
import { useUser } from 'zustand-stores/userEnvironmentStore';
import {
  markNotificationsAsViewed,
  useUserNotifications,
} from 'zustand-stores/userNoficationsStore';

export function NotificationsDropdownContainer() {
  const { user } = useUser();
  const { notifications } = useUserNotifications();

  // Add/Remove notifications here to show/hide them in the dropdown
  const displayLocalDropdownNotifications: Record<
    LocalDropdownNotificationType | string,
    boolean
  > = useMemo(
    () => ({
      [LocalDropdownNotificationType.ORG_ADMIN_USER_DROPDOWN_ATS_CTA]:
        promoteAts({ user }),
      [LocalDropdownNotificationType.OFFICE_HOURS]: promoteOfficeHours({
        user,
      }),
      [LocalDropdownNotificationType.OFFICE_HOURS_07_2024]:
        promoteOfficeHoursApril24({ user }),
      [LocalDropdownNotificationType.RESUME_WEBINAR]: promoteResumeWebinar({
        user,
      }),
      [LocalDropdownNotificationType.INTERVIEW_WEBINAR_08_2024]:
        promoteWebinarAugust2024({ user }),
      [LocalDropdownNotificationType.ES_SURVEY]: promoteEsSurvey({
        user,
      }),
      [LocalDropdownNotificationType.GF_2024_TEXT]: promoteGradfair2024(
        user,
        new Date('2024-09-04T00:00:00.000-04:00'),
        new Date('2024-09-25T00:00:00.000-04:00'),
      ),
      [LocalDropdownNotificationType.GF_2024_CALENDAR]: promoteGradfair2024(
        user,
        new Date('2024-09-25T00:00:00.000-04:00'),
        new Date('2024-10-09T00:00:00.000-04:00'),
      ),
      [LocalDropdownNotificationType.ADMIN_SURVEY_2024]:
        promoteAdminSurvey2024(user),
    }),
    [user],
  );

  const atsNotification = useLocalNotification(
    LocalDropdownNotificationType.ORG_ADMIN_USER_DROPDOWN_ATS_CTA,
  );

  const shouldDisplayAtsNotification = useMemo(
    () =>
      atsNotification?.status === LocalNotificationStatus.NEW &&
      promoteAts({ user }),
    [atsNotification?.status, user],
  );

  useFetchNotificationsOnPageChange();

  const mappedLocalNotifications = useMappedLocalNotifications();

  // Filter local notifications to only show the ones that
  // should be displayed in the dropdown
  const localNotificationsFiltered = useMemo(
    () =>
      mappedLocalNotifications.filter(
        (notification) =>
          notification.status === LocalNotificationStatus.NEW &&
          Object.hasOwn(displayLocalDropdownNotifications, notification.type) &&
          displayLocalDropdownNotifications[notification.type],
      ),
    [mappedLocalNotifications, displayLocalDropdownNotifications],
  );

  const onToggle = useCallback(
    (open: boolean) => {
      if (!open) return;

      markNotificationsAsViewed(notifications);

      const notificationTypes = new Set<
        | 'APPLICANT_TRACKER_AD'
        | ApiNotification['type']
        | MappedNotification['type']
        | MappedLocalNotification['type']
        | ApiConfiguredNotification['data']['details']['analyticsName']
      >(
        notifications.map((notification) => {
          if (notification.type === 'CONFIGURED_NOTIFICATION') {
            return notification.data.details.analyticsName;
          }
          return notification.type;
        }),
      );

      if (shouldDisplayAtsNotification) {
        notificationTypes.add('APPLICANT_TRACKER_AD');
      }

      if (localNotificationsFiltered.length > 0) {
        localNotificationsFiltered.forEach((notification) => {
          notificationTypes.add(notification.type);
        });
      }

      markLocalNotificationsAsViewed(
        localNotificationsFiltered.map((notification) => notification.type),
      );

      trackEvent('Displayed User Menu Notifications', {
        notification_types: Array.from(notificationTypes),
      });
    },
    [shouldDisplayAtsNotification, notifications, localNotificationsFiltered],
  );

  return (
    <NotificationsDropdown
      notifications={notifications}
      localNotifications={localNotificationsFiltered}
      onToggle={onToggle}
      onClickTrigger={() => trackClicked('Header Utility CTA - Notifications')}
    />
  );
}
